import React, { ReactNode, useMemo } from "react";
import {
  createStyles,
  withStyles,
  WithStyles,
} from "@mui/styles";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  PaperProps,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import Draggable from "react-draggable";
import $ from "jquery";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment/moment";
import clsx from "clsx";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Serie } from "@nivo/line";
import UsageLineChart from "../charts/UsageLineChart";
import {
  buildLegendConfigs,
  getChartBottomMargin,
  getUniqueYearsFromSeries,
  resolveSeriesColors,
} from "../charts/chartUtils";
import "./ExportDialog.scss";
import { DATASET_NAME, LEGAL_TEXT } from "../../../config";
import GetAppIcon from "@mui/icons-material/GetApp";
import { VETSTAT_UI } from "../../strings";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      cursor: "move",
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

export type FilterSummaryEntry = {
  label: string;
  values: string[];
};

type ExportDialogProps = {
  disabled: boolean;
  disabledReason?: ReactNode;
  series: Serie[];
  filtersSummary?: FilterSummaryEntry[];
  usageQuery: string;
  baseURL: string;
};

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const ExportDialog = ({
  disabled,
  disabledReason,
  series,
  filtersSummary = [],
  usageQuery,
  baseURL,
}: ExportDialogProps) => {
  const datasetSlug = DATASET_NAME.toLowerCase().replace(/\s+/g, "_");
  const initialState = {
    open: false,
    chartTitle: `${DATASET_NAME} - ${VETSTAT_UI.chartExport.defaultChartTitleSuffix}`,
    chartSubtitle: VETSTAT_UI.chartExport.defaultChartSubtitle,
    sourceLabel: moment().format("YYYY MMM"),
    skipEverySecondXTick: false,
    tickRotation: 0,
    width: "fullWidth",
    format: "jpg",
    showLegend: true,
    showGridLines: true,
    yAxisTickInterval: "" as number | "",
  };
  const [open, setOpen] = React.useState(initialState.open);
  const [chartTitle, setChartTitle] = React.useState<string>(
    initialState.chartTitle
  );
  const [chartSubtitle, setChartSubtitle] = React.useState<string>(
    initialState.chartSubtitle
  );
  const [sourceLabel, setSourceLabel] = React.useState<string>(
    initialState.sourceLabel
  );
  const [skipEverySecondXTick, setShowEverySecondXTick] = React.useState(
    initialState.skipEverySecondXTick
  );
  const [tickRotation, setTickRotation] = React.useState(
    initialState.tickRotation
  );
  const [width, setWidth] = React.useState(initialState.width);
  const [format, setFormat] = React.useState(initialState.format);
  const [showLegend, setShowLegend] = React.useState(initialState.showLegend);
  const [showGridLines, setShowGridLines] = React.useState(
    initialState.showGridLines
  );
  const [yAxisTickInterval, setYAxisTickInterval] = React.useState<number | "">(
    initialState.yAxisTickInterval
  );
  const [datasetDownloading, setDatasetDownloading] = React.useState(false);

  const resolvedColors = useMemo(
    () => resolveSeriesColors(series),
    [series]
  );
  const legendConfigs = useMemo(
    () => buildLegendConfigs(series, resolvedColors),
    [series, resolvedColors]
  );
  const chartBottomMargin = useMemo(
    () => getChartBottomMargin(legendConfigs.length),
    [legendConfigs.length]
  );
  const previewChartBottomMargin = useMemo(
    () =>
      showLegend
        ? chartBottomMargin
        : getChartBottomMargin(0),
    [chartBottomMargin, showLegend]
  );
  const hasSeriesData = useMemo(
    () => series.some((entry) => entry.data.length > 0),
    [series]
  );
  const availableYears = useMemo(
    () => getUniqueYearsFromSeries(series),
    [series]
  );
  const filteredTickValues = useMemo(
    () =>
      skipEverySecondXTick
        ? availableYears.filter((_year, index) => index % 2 === 0)
        : undefined,
    [skipEverySecondXTick, availableYears]
  );

  const filteredYTickValues = useMemo(() => {
    if (typeof yAxisTickInterval !== "number" || yAxisTickInterval <= 0) {
      return undefined;
    }
    let max = 0;
    series.forEach((serie) => {
      serie.data.forEach((d) => {
        const val = Number(d.y);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });

    if (max === 0) return undefined;

    const ticks = [];
    for (let i = 0; i <= max + yAxisTickInterval; i += yAxisTickInterval) {
      ticks.push(i);
      if (i > max) break;
    }
    return ticks;
  }, [yAxisTickInterval, series]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetState();
  };
  const handleChangeChartTitle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setChartTitle(event.target.value);
  const handleChangeChartSubtitle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setChartSubtitle(event.target.value);
  const handleChangeSourceLabel = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setSourceLabel(event.target.value);
  const handleChangeShowEverySecondXTick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setShowEverySecondXTick(event.target.checked);
  const handleChangeTickRotation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(event.target.value);
    setTickRotation(Number.isNaN(value) ? 0 : value);
  };
  const handleChangeYAxisTickInterval = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "") {
      setYAxisTickInterval("");
    } else {
      const numValue = Number(value);
      setYAxisTickInterval(Number.isNaN(numValue) ? "" : numValue);
    }
  };
  const handleChangeWidth = (event: SelectChangeEvent<string>) =>
    setWidth(event.target.value as string);
  const handleChangeFormat = (event: SelectChangeEvent<string>) =>
    setFormat(event.target.value as string);
  const handleToggleLegend = (event: React.ChangeEvent<HTMLInputElement>) =>
    setShowLegend(event.target.checked);
  const handleToggleGridLines = () =>
    setShowGridLines((current) => !current);

  const resetState = () => {
    setChartTitle(initialState.chartTitle);
    setChartSubtitle(initialState.chartSubtitle);
    setSourceLabel(initialState.sourceLabel);
    setShowEverySecondXTick(initialState.skipEverySecondXTick);
    setTickRotation(initialState.tickRotation);
    setWidth(initialState.width);
    setFormat(initialState.format);
    setShowLegend(initialState.showLegend);
    setShowGridLines(initialState.showGridLines);
    setYAxisTickInterval(initialState.yAxisTickInterval);
  };

  const saveAs = (blob: string, fileName: string) => {
    const link = document.createElement("a");
    link.download = fileName;
    link.href = blob;
    link.click();
  };

  const exportImage = () => {
    html2canvas(document.getElementById("componentToPrint")!, {
      scale: 10,
      logging: false,
    }).then((canvas) => {
      const fileBase = `${datasetSlug}_chart`;
      if (format === "jpg") {
        const jpeg = canvas.toDataURL("image/jpeg");
        saveAs(jpeg, `${fileBase}.jpg`);
      }
      if (format === "png") {
        const png = canvas.toDataURL("image/png");
        saveAs(png, `${fileBase}.png`);
      }
    });
  };

  const exportPDF = () => {
    const componentToPrint = $("#componentToPrint");
    const divHeight = componentToPrint.height();
    const divWidth = componentToPrint.width();
    const ratio = (divHeight ?? 1) / (divWidth ?? 1);
    html2canvas(document.getElementById("componentToPrint")!, {
      scale: 10,
      logging: false,
    }).then((canvas) => {
      const jpeg = canvas.toDataURL("image/jpeg");
      const doc = new jsPDF("l", "px");
      const docWidth = doc.internal.pageSize.getWidth();
      const docHeight = doc.internal.pageSize.getHeight();
      const chartHeight = 0.583 * docHeight;
      const chartWidth = chartHeight / ratio;
      const fontName = "helvetica";
      const fontStyle = "";
      const leftMargin = 25;
      const upperMargin = 30;
      const valueTextsStartX = 75;
      const filtersStartY = 60;
      const valueTextSpaceY = 15;
      const titleFontSize = 16;
      const textFontSize = 10;
      const legalTextFontSize = 9;

      doc
        .setFont(fontName, fontStyle, "normal")
        .setFontSize(titleFontSize)
        .text(chartTitle || "[Title]", docWidth / 2, upperMargin, {
          align: "center",
        })
        .setFontSize(textFontSize)
        .text(chartSubtitle || "[Subtitle]", docWidth / 2, upperMargin + 18, {
          align: "center",
        });

      const summaryEntries = filtersSummary.filter(
        (entry) => entry?.label?.length
      );
      let summaryCursor = filtersStartY;
      summaryEntries.forEach((entry) => {
        const valuesText = entry.values.length
          ? entry.values.join(", ")
          : "No selection";
        const wrappedValues = doc.splitTextToSize(
          valuesText,
          docWidth - valueTextsStartX - leftMargin
        );
        doc
          .setFont(fontName, fontStyle, "bold")
          .text(`${entry.label}:`, leftMargin, summaryCursor);
        doc
          .setFont(fontName, fontStyle, "normal")
          .text(wrappedValues, valueTextsStartX, summaryCursor);
        summaryCursor += valueTextSpaceY * Math.max(wrappedValues.length, 1);
      });

      const chartTop = Math.min(
        Math.max(summaryCursor + 20, docHeight / 2 - chartHeight / 2),
        docHeight - chartHeight - upperMargin
      );
      doc.addImage(
        jpeg,
        "JPEG",
        docWidth / 2 - chartWidth / 2,
        chartTop,
        chartWidth,
        chartHeight
      );

      const footerY = docHeight - upperMargin;
      const legalLines = doc.splitTextToSize(
        LEGAL_TEXT,
        docWidth - 2 * leftMargin
      );
      doc
        .setFont(fontName, fontStyle, "italic")
        .setFontSize(legalTextFontSize)
        .text(`${sourceLabel}`, leftMargin, footerY)
        .text(legalLines, leftMargin, footerY + 12)
        .text(moment().format("YYYY-MM-DD HH:mm"), docWidth - 140, footerY);
      doc.save(`${datasetSlug}_chart.pdf`);
    });
  };

  const convertSeriesToCsv = (seriesData: Serie[]): string => {
    const header = "serie,year,value";
    const rows = seriesData.flatMap((entry) =>
      entry.data.map((point) => {
        const year = typeof point.x === "number" ? point.x : Number(point.x);
        const value = typeof point.y === "number" ? point.y : Number(point.y);
        return `${JSON.stringify(entry.id)},${year},${value}`;
      })
    );
    return [header, ...rows].join("\n");
  };
  const downloadDataset = async () => {
    const trimmedBaseURL = baseURL ? baseURL.replace(/\/$/, "") : "";
    const endpoint = trimmedBaseURL
      ? `${trimmedBaseURL}/api/usage_by_year`
      : "/api/vetstat/usage_by_year";
    const url = usageQuery ? `${endpoint}?${usageQuery}` : endpoint;
    setDatasetDownloading(true);
    try {
      const response = await fetch(url);
      const payload: { series?: Serie[]; detail?: string } =
        await response.json();
      if (!response.ok) {
        throw new Error(payload.detail ?? VETSTAT_UI.chartExport.fetchDatasetFailed);
      }
      const csv = convertSeriesToCsv(payload.series ?? []);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const blobUrl = URL.createObjectURL(blob);
      saveAs(blobUrl, `${datasetSlug}_data.csv`);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      alert(
        err instanceof Error ? err.message : VETSTAT_UI.chartExport.downloadErrorGeneric
      );
    } finally {
      setDatasetDownloading(false);
    }
  };

  const handleClickReset = () => resetState();
  const isExportDisabled = disabled || !hasSeriesData;
  const tooltipTitle = isExportDisabled
    ? disabledReason ?? VETSTAT_UI.chartExport.exportDisabledReasonDefault
    : VETSTAT_UI.chartExport.exportChartTooltip;
  const datasetDisabled = isExportDisabled || datasetDownloading;
  const datasetTooltip = datasetDownloading
    ? VETSTAT_UI.chartExport.downloadingDataset
    : VETSTAT_UI.chartExport.downloadWithFilters;

  return (
    <div className="exportButtonGroup">
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            className="btnExportPDF"
            variant="outlined"
            disableElevation
            onClick={handleClickOpen}
            color="primary"
            startIcon={<EqualizerIcon />}
            disabled={isExportDisabled}
          >
              {VETSTAT_UI.chartExport.exportChart}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={datasetTooltip}>
        <span>
          <Button
            className="btnExportData"
            variant="outlined"
            disableElevation
            color="primary"
            startIcon={<GetAppIcon />}
            disabled={datasetDisabled}
            onClick={downloadDataset}
          >
            {datasetDownloading ? VETSTAT_UI.chartExport.downloading : VETSTAT_UI.chartExport.downloadDataset}
          </Button>
        </span>
      </Tooltip>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        PaperComponent={PaperComponent}
        maxWidth="lg"
      >
        <DialogTitle id="draggable-dialog-title" onClose={handleClose}>
          {VETSTAT_UI.chartExport.exportConfigTitle}
        </DialogTitle>
        <DialogContent dividers className="dialogContentContainer">
          <div className={clsx("settingsElement", "previewContainer")}>
            <span className="previewTitleContainer">
              <Divider flexItem />
              <Typography className="previewTitle">Preview</Typography>
              <Divider flexItem />
            </span>
            <div
              id="componentToPrint"
              className={clsx(
                "contentToPrint",
                width === "halfWidth" && "halfPage"
              )}
            >
              <Typography className="sourceLabel">
                {sourceLabel}
              </Typography>
              <Typography className="chartTitle">{chartTitle}</Typography>
              <Typography className="chartSubTitle">
                {chartSubtitle}
              </Typography>
              <div className="chartArea">
                {hasSeriesData ? (
                  <UsageLineChart
                    data={series}
                    colors={resolvedColors}
                    legendConfigs={legendConfigs}
                    chartBottomMargin={previewChartBottomMargin}
                    tickRotation={tickRotation}
                    tickValues={filteredTickValues}
                    yTickValues={filteredYTickValues}
                    isInteractive={false}
                    height="100%"
                    showLegend={showLegend}
                    showGridX={showGridLines}
                    showGridY={showGridLines}
                  />
                ) : (
                  <Typography align="center" variant="body2">
                    {VETSTAT_UI.chartExport.noDataToDisplay}
                  </Typography>
                )}
              </div>
            </div>
          </div>
          <Divider orientation="vertical" flexItem />
          <div className="settingsContainer">
            <span className={clsx("settingsTitleContainer", "settingsElement")}>
              <Typography className="settingsTitle">{VETSTAT_UI.chartExport.settings}</Typography>
              <Tooltip title={VETSTAT_UI.chartExport.resetSettings} placement="bottom-end">
                <IconButton
                  color="secondary"
                  aria-label="reset settings to default"
                  size="small"
                  onClick={handleClickReset}
                >
                  <RefreshIcon style={{ transform: "scaleX(-1)" }} />
                </IconButton>
              </Tooltip>
            </span>
            <TextField
              id="chart-title-input"
              label={VETSTAT_UI.chartExport.title}
              className="settingsElement"
              size="small"
              value={chartTitle}
              onChange={handleChangeChartTitle}
            />
            <TextField
              id="chart-subtitle-input"
              label={VETSTAT_UI.chartExport.subtitle}
              className="settingsElement"
              size="small"
              value={chartSubtitle}
              onChange={handleChangeChartSubtitle}
            />
            <TextField
              id="source-label-input"
              label={VETSTAT_UI.chartExport.source}
              className="settingsElement"
              size="small"
              value={sourceLabel}
              onChange={handleChangeSourceLabel}
            />
            <TextField
              id="tick-rotation-input"
              label={VETSTAT_UI.chartExport.yearRotation}
              className="settingsElement"
              size="small"
              value={tickRotation}
              type="number"
              onChange={handleChangeTickRotation}
            />
            <TextField
              id="y-axis-interval-input"
              label={VETSTAT_UI.chartExport.yAxisInterval}
              className="settingsElement"
              size="small"
              value={yAxisTickInterval}
              type="number"
              onChange={handleChangeYAxisTickInterval}
              placeholder="Auto"
            />
            <FormControlLabel
              label={VETSTAT_UI.chartExport.skipEveryOtherYear}
              className="settingsElement"
              control={
                <Checkbox
                  checked={skipEverySecondXTick}
                  onChange={handleChangeShowEverySecondXTick}
                  name="showEverySecondXTick"
                  color="primary"
                  size="small"
                />
              }
            />
            <FormControl variant="outlined" className="settingsElement" size="small">
              <InputLabel id="width-selector-label">{VETSTAT_UI.chartExport.width}</InputLabel>
              <Select
                labelId="width-selector-label"
                id="width-selector"
                value={width}
                onChange={handleChangeWidth}
                label={VETSTAT_UI.chartExport.width}
              >
                <MenuItem value={"halfWidth"}>{VETSTAT_UI.chartExport.halfPage}</MenuItem>
                <MenuItem value={"fullWidth"}>{VETSTAT_UI.chartExport.fullWidth}</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" className="settingsElement" size="small">
              <InputLabel id="format-selector-label">Format</InputLabel>
              <Select
                labelId="format-selector-label"
                id="format-selector"
                value={format}
                onChange={handleChangeFormat}
                label="Format"
              >
                <MenuItem value={"jpg"}>JPG</MenuItem>
                <MenuItem value={"png"}>PNG</MenuItem>
                <MenuItem value={"pdf"}>PDF</MenuItem>
              </Select>
            </FormControl>
            {format === "pdf" && (
              <FormHelperText className="pdfHelperText">
                {VETSTAT_UI.chartExport.pdfIncludesFilters}
              </FormHelperText>
            )}
            <FormControlLabel
              label={VETSTAT_UI.chartExport.showLegend}
              className="settingsElement"
              control={
                <Checkbox
                  checked={showLegend}
                  onChange={handleToggleLegend}
                  name="showLegend"
                  color="primary"
                  size="small"
                />
              }
            />
            <FormControlLabel
              label={VETSTAT_UI.chartExport.showGrid}
              className="settingsElement"
              control={
                <Checkbox
                  checked={showGridLines}
                  onChange={handleToggleGridLines}
                  name="showGridLines"
                  color="primary"
                  size="small"
                />
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={format === "pdf" ? exportPDF : exportImage}
            color="primary"
            disableElevation
            variant="contained"
          >
            {VETSTAT_UI.chartExport.exportAs} {format.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ExportDialog;
